export interface SmsRequestBody {
  phoneE164: string;
}

export interface SmsRequestResponse {
  challengeId: string;
}

export interface SmsVerifyBody {
  challengeId: string;
  code: string;
  deviceId: string;
  signaturePublicKey: string;
  displayName?: string | undefined;
  // Optional client-supplied 32-byte device id (the MLS SDK's
  // `ownDeviceId`). When provided, the BE issues a JWT whose
  // `device_id` claim matches these bytes — required for the
  // SDK's emitted envelopes to pass `sender_device` validation on
  // `POST /v1/messages`.
  sdkDeviceIdBytes?: Uint8Array;
  // 'signin' refuses to create a missing account (BE → account_not_found);
  // 'signup'/undefined keeps the legacy create-or-sign-in behaviour.
  intent?: 'signin' | 'signup' | undefined;
}

export interface SmsVerifyResponse {
  accessToken: string;
  refreshToken: string;
  accountId: string;
  deviceId: string;
  // Access-token lifetime in seconds (BE `expires_in`). Threaded into
  // setAccessToken so the proactive pre-expiry refresh actually runs;
  // without it the session only ever refreshes reactively on a 401.
  expiresIn?: number;
  // Returning user's persisted display_name from the BE profile
  // row. Absent (or empty string) for a brand-new account that
  // hasn't completed the sign-up display-name step yet.
  displayName?: string;
  // True when the BE marked the account as needing an initial
  // recovery backup upload. Fresh signups always see this true;
  // returning sign-ins see it false (the backfill in migration
  // 0014 stamped existing accounts as already-backed-up). Drives
  // the post-bootstrap RecoveryBackupSetupHost gate in App.tsx.
  recoveryBackupRequired?: boolean;
}

// Email-channel equivalents of the SMS shapes. The verify body and
// response are byte-identical to the SMS variants (the challenge id is
// channel-blind once minted server-side); the request body just carries
// the email instead of the phone number.
export interface EmailRequestBody {
  email: string;
}

export interface EmailRequestResponse {
  challengeId: string;
}

export interface EmailVerifyBody {
  challengeId: string;
  code: string;
  deviceId: string;
  signaturePublicKey: string;
  displayName?: string | undefined;
  // Optional client-supplied 32-byte device id (the MLS SDK's
  // `ownDeviceId`). When provided, the BE issues a JWT whose
  // `device_id` claim matches these bytes — required for the
  // SDK's emitted envelopes to pass `sender_device` validation on
  // `POST /v1/messages`.
  sdkDeviceIdBytes?: Uint8Array;
  // 'signin' refuses to create a missing account (BE → account_not_found);
  // 'signup'/undefined keeps the legacy create-or-sign-in behaviour.
  intent?: 'signin' | 'signup' | undefined;
}

export interface EmailVerifyResponse {
  accessToken: string;
  refreshToken: string;
  accountId: string;
  deviceId: string;
  // Access-token lifetime in seconds (BE `expires_in`); see SmsVerifyResponse.
  expiresIn?: number;
  displayName?: string;
  recoveryBackupRequired?: boolean;
}

export interface RefreshBody {
  refreshToken: string;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  /**
   * Access-token lifetime in seconds, echoed from the BE's
   * `expires_in`. Drives proactive refresh so a request never fires
   * with an already-dead access token. Absent for older BEs.
   */
  expiresIn?: number;
}

export interface AuthError {
  code: string;
  message: string;
  retryAfterSeconds?: number;
}

export class NetworkError extends Error {
  /**
   * Server-shaped error code parsed from the response body
   * (`{ error: string, ... }` on most 4xx). Lets callers distinguish
   * between e.g. `identity_mismatch` and `invalid_code` on the same
   * 403 — both look identical at the status-code layer but mean very
   * different things in the verify flow.
   */
  readonly serverError?: string;
  /**
   * Human-readable message from the body's `message` field, when the
   * BE supplies one. Surfaced to the UI as a fallback display string
   * when no specific failure-reason mapping exists.
   */
  readonly serverMessage?: string;

  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly retryAfter?: number,
    body?: { error?: string; message?: string } | undefined,
  ) {
    super(message);
    this.name = 'NetworkError';
    if (body?.error) this.serverError = body.error;
    if (body?.message) this.serverMessage = body.message;
  }
}
